<template>
  <div class="login-page">
    <div class="container-xl">
      <section class="row align-items-center vh-100">
        <div
          class="login-form col-12 col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-lg-5 offset-lg-7 col-xl-4 offset-xl-8"
        >
          <div class="d-flex justify-content-between">
            <router-link to="/main/herbarium/grid">
              <img
                src="../assets/icon/icon-herbarium.svg"
                alt="logo-herbarium"
                class="logo mt-2 mt-md-4"
              />
            </router-link>
            <LanguageSwitcher class="align-self-center mt-3" />
          </div>

          <h4 class="my-4">{{ $t('register_page_title') }}</h4>
          <div class="social-block">
            <b-button
              block
              variant="link"
              :disabled="isLoading"
              @click="signInGoogle()"
            >
              <img
                src="../assets/icon/icon-google.svg"
                alt="logo-google"
                class="mr-1"
              />
              {{ $t('login_page_btn_sign_google') }}
            </b-button>
            <b-button
              block
              variant="link"
              :disabled="isLoading"
              @click="signInFacebook()"
            >
              <img
                src="../assets/icon/icon-fb.svg"
                alt="logo-fb"
                class="mr-1"
              />
              {{ $t('login_page_btn_sign_facebook') }}
            </b-button>
          </div>
          <b-form class="login-page-form mt-3" @submit.prevent="registerUser()">
            <b-form-group
              :label="$t('login_page_input_email_label')"
              label-for="email"
              role="group"
            >
              <b-form-input
                id="email"
                v-model.trim="$v.user.email.$model"
                :state="validateState('email')"
              />
              <template v-if="$v.user.email.$dirty && $v.user.email.$invalid">
                <b-form-text v-if="!$v.user.email.email" text-variant="danger">
                  {{ $t('login_page_input_email_error_valid') }}
                </b-form-text>
                <b-form-text
                  v-if="!$v.user.email.required"
                  text-variant="danger"
                >
                  {{ $t('profile_info_confirm_password_required_error') }}
                </b-form-text>
              </template>
            </b-form-group>
            <b-form-group
              :label="$t('register_page_input_name_label')"
              label-for="name"
              role="group"
            >
              <b-form-input
                id="name"
                v-model.trim="$v.user.firstname.$model"
                :state="validateState('firstname')"
                type="text"
              />
              <template
                v-if="$v.user.firstname.$dirty && $v.user.firstname.$invalid"
              >
                <b-form-text>
                  v-if="!$v.user.firstname.required" text-variant="danger" >
                  {{ $t('profile_info_confirm_password_required_error') }}
                </b-form-text>
              </template>
            </b-form-group>
            <b-form-group
              :label="$t('login_page_input_password_label')"
              label-for="password"
            >
              <b-form-input
                id="password"
                v-model.trim="$v.user.password.$model"
                :state="validateState('password')"
                type="password"
                aria-describedby="password-feedback"
              />
              <template
                v-if="$v.user.password.$dirty && $v.user.password.$invalid"
              >
                <b-form-text
                  v-if="!$v.user.password.alphaNumRegex"
                  text-variant="danger"
                >
                  {{ $t('profile_info_confirm_password_alphanum_error') }}
                </b-form-text>
                <b-form-text
                  v-if="!$v.user.password.minLength"
                  text-variant="danger"
                >
                  {{ $t('profile_info_confirm_password_min_length_error') }}
                </b-form-text>
                <b-form-text
                  v-if="!$v.user.password.required"
                  text-variant="danger"
                >
                  {{ $t('profile_info_confirm_password_required_error') }}
                </b-form-text>
              </template>
            </b-form-group>
            <b-button
              type="submit"
              variant="success"
              block
              class="mt-2"
              :disabled="isLoading"
            >
              <b-spinner v-if="isLoading" show small type="grow" />
              {{ $t('register_page_btn_register') }}
            </b-button>
          </b-form>
          <p class="text-center my-2 my-md-3">
            {{ $t('register_page_text_sign_in') }}
            <router-link to="/login" class="text-success">
              {{ $t('register_page_link_sign_in') }}
            </router-link>
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, minLength, email, helpers } from 'vuelidate/lib/validators'
const alphaNumRegex = helpers.regex(
  'alphaNumRegex',
  /^(?=.*\d)(?=.*[a-zA-Z]).{6,20}$/
)
import config from '../config'
import LanguageSwitcher from '@/components/LanguageSwitcher'

export default {
  components: { LanguageSwitcher },
  data() {
    return {
      user: {
        firstname: '',
        email: '',
        password: ''
      }
    }
  },
  validations: {
    user: {
      firstname: { required },
      email: { required, email },
      password: { required, alphaNumRegex, minLength: minLength(6) }
    }
  },
  computed: mapGetters(['errorAuth', 'getResponse', 'isLoading']),
  mounted() {
    this.clearError()
    window.gapi.load('auth2', function() {
      window.gapi.auth2
        .init({
          client_id: config.GOOGLE.client_id
        })
        .then(
          () => console.log('Init Ok'), // eslint-disable-line no-console
          err => console.log('Init fail', err) // eslint-disable-line no-console
        )
    })

    window.FB.init({
      appId: config.FACEBOOK.appId,
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v6.0'
    })
  },
  methods: {
    ...mapActions(['registerUser', 'clearError']),
    registerUser() {
      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }
      this.$store
        .dispatch('registerUser', this.user)
        .then(
          () =>
            (this.user = {
              firstname: '',
              email: '',
              password: ''
            })
        )
        .then(() => {
          this.$v.user.$reset()
        })
    },
    validateState(name) {
      const { $dirty, $error, $model, $invalid } = this.$v.user[name]
      if (!$model.length && !$invalid) return null
      return $dirty ? !$error : null
    },
    async signInGoogle() {
      const GoogleAuth = window.gapi.auth2.getAuthInstance()
      const gUser = await GoogleAuth.signIn({ scope: 'profile email' })
      const user = {
        email: gUser.getBasicProfile().getEmail(),
        id: gUser.getBasicProfile().getId(),
        name: gUser.getBasicProfile().getName()
      }

      this.$store
        .dispatch('loginUser', { path: '/auth/google', user })
        .then(() => {
          if (!this.errorAuth) {
            this.$router.push('/main/herbarium/grid')
          }
        })
    },
    signInFacebook() {
      const saveUser = user => {
        this.$store
          .dispatch('loginUser', { path: '/auth/facebook', user })
          .then(() => {
            if (!this.errorAuth) {
              this.$router.push('/main/herbarium/grid')
            }
          })
      }
      //Вылетает ворнинг который ругается на токен , по идеи после верификации приложения фейсбуком
      //должен исчезнуть , но это не точно , пока других способов решения нет
      window.FB.login(
        function(response) {
          if (response.authResponse) {
            //Вылетае ворнинг в консоль потому что пока приложение работает не через https
            window.FB.api(`/me`, 'GET', { fields: 'email,first_name' }, res =>
              saveUser(res)
            )
          } else {
            console.log('User cancelled login or did not fully authorize.') // eslint-disable-line no-console
          }
        },
        { scope: 'email' }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.login-page {
  text-align: left;
  font-family: 'Roboto', sans-serif;
}
.social-block {
  button {
    background: rgba(81, 142, 248, 0.1);
    border-radius: 8px;
  }
}
.row {
  margin-right: 0;
  margin-left: 0;
}
.invalid-feedback {
  line-height: 1;
  width: 100%;
  margin-top: 0.1rem;
  color: #dc3545;
}
@media (min-width: 992px) {
  .login-page {
    background: url('../assets/login-page/login-bg.svg') center top/cover
      no-repeat;
  }
  .login-form {
    background: #ffffff;
    box-shadow: 9px 9px 40px rgba(0, 0, 0, 0.12);
    border-radius: 15px;
  }
  .invalid-feedback {
    font-size: 70%;
  }
}
@media (min-width: 1500px) {
  .login-form {
    /deep/ .logo {
      margin-top: 2rem !important;
    }
    /deep/ .register {
      margin-bottom: 1rem !important;
    }
  }
}
</style>
